import { useState, useEffect, useCallback, useRef } from 'react'
import { airdropApi, walletApi } from '../api/endpoints'
import { adFailMessage, preloadRewardedAd, showRewardedAd } from '../lib/ads'
import {
  AirdropStatusResponse,
  AirdropClickResponse,
  AirdropLeaderboardEntry,
  CoinMap,
  WSBroadcastScore,
  WSHubMessage,
} from '../types'

const WS_BASE_URL = import.meta.env.DEV
  ? 'ws://localhost:8000'
  : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`

const WS_RECONNECT_INITIAL_DELAY = 1000
const WS_RECONNECT_MAX_DELAY = 30000
const WS_RECONNECT_MAX_ATTEMPTS = 10

const TOKEN_KEY = 'access_token'

type PendingClick = {
  resolve: (result: AirdropClickResponse | null) => void
  timer: ReturnType<typeof setTimeout>
}

export function useAirdrop() {
  const [status, setStatus] = useState<AirdropStatusResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [wallet, setWallet] = useState<{ coins: CoinMap; dollar: number } | null>(null)
  const [leaderboard, setLeaderboard] = useState<AirdropLeaderboardEntry[]>([])
  const [liveScore, setLiveScore] = useState<WSBroadcastScore | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const pendingClicksRef = useRef<Map<string, PendingClick>>(new Map())
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptRef = useRef(0)

  const clearPendingClicks = useCallback(() => {
    for (const [requestID, pending] of pendingClicksRef.current) {
      clearTimeout(pending.timer)
      pending.resolve(null)
      pendingClicksRef.current.delete(requestID)
    }
  }, [])

  const failPendingClicks = useCallback((message: string) => {
    clearPendingClicks()
    setError(message)
  }, [clearPendingClicks])

  const fetchStatus = useCallback(async (opts?: { silent?: boolean }) => {
    try {
      if (!opts?.silent) setLoading(true)
      setError(null)
      const data = await airdropApi.getStatus()
      setStatus(data)
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در بارگذاری وضعیت ایردراپ')
    } finally {
      if (!opts?.silent) setLoading(false)
    }
  }, [])

  const fetchWallet = useCallback(async () => {
    try {
      const data = await walletApi.getCoins()
      setWallet(data)
    } catch (err: any) {
      console.error('خطا در بارگذاری کیف پول:', err)
    }
  }, [])

  const click = useCallback(async (): Promise<AirdropClickResponse | null> => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setError('وب‌سوکت ایردراپ متصل نیست')
      return null
    }

    const requestID = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    return new Promise(resolve => {
      const timer = setTimeout(() => {
        pendingClicksRef.current.delete(requestID)
        setError('زمان ثبت کلیک به پایان رسید')
        resolve(null)
      }, 10000)

      pendingClicksRef.current.set(requestID, { resolve, timer })
      try {
        if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) {
          throw new Error('WebSocket closed')
        }
        ws.send(JSON.stringify({
          type: 'airdrop_click',
          request_id: requestID,
        }))
      } catch {
        clearTimeout(timer)
        pendingClicksRef.current.delete(requestID)
        setError('خطا در ارسال کلیک')
        resolve(null)
      }
    })
  }, [])

  const fetchLeaderboard = useCallback(async (limit: number = 50): Promise<AirdropLeaderboardEntry[]> => {
    try {
      const res = await airdropApi.getLeaderboard(limit)
      setLeaderboard(res.winners)
      return res.winners
    } catch {
      return []
    }
  }, [])

  const fetchMonthlyLeaderboard = useCallback(async (date?: string, limit: number = 50): Promise<AirdropLeaderboardEntry[]> => {
    try {
      const res = await airdropApi.getMonthlyLeaderboard(date, limit)
      return res.winners
    } catch {
      return []
    }
  }, [])

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) {
      console.warn('No token found, WebSocket connection skipped')
      return
    }

    let disposed = false
    let ws: WebSocket | null = null

    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
    }

    const connect = () => {
      if (disposed) return
      clearReconnectTimer()

      const current = wsRef.current
      if (current && (current.readyState === WebSocket.OPEN || current.readyState === WebSocket.CONNECTING)) return
      if (current && current.readyState === WebSocket.CLOSING) return

      const currentToken = localStorage.getItem(TOKEN_KEY)
      if (!currentToken) {
        setIsConnected(false)
        return
      }

      const socket = new WebSocket(`${WS_BASE_URL}/api/ws/airdrop`)
      ws = socket
      wsRef.current = socket

      socket.onopen = () => {
        if (disposed || wsRef.current !== socket) {
          socket.close()
          return
        }
        // Send authorization after connection
        socket.send(JSON.stringify({
          type: 'auth',
          token: currentToken,
        }))
        reconnectAttemptRef.current = 0
        setError(null)
        setIsConnected(true)
      }

      socket.onmessage = (event: MessageEvent) => {
        if (disposed || wsRef.current !== socket) return

        try {
          const msg: WSHubMessage = JSON.parse(event.data)

          if (msg.type === 'auth_response') {
            if (!msg.success) {
              console.error('WebSocket auth failed:', msg.error)
              socket.close()
            }
            return
          }

          if (msg.type === 'airdrop_click_response' && msg.request_id) {
            const pending = pendingClicksRef.current.get(msg.request_id)
            if (pending) {
              clearTimeout(pending.timer)
              pendingClicksRef.current.delete(msg.request_id)
              const result: AirdropClickResponse = {
                score: msg.score ?? 0,
                can_click: msg.can_click ?? false,
                is_game_open: msg.is_game_open ?? false,
                seconds_remaining: msg.seconds_remaining,
                score_updated: msg.success === true,
                message: msg.message || '',
              }
              setError(null)
              setStatus(prev => {
                if (!prev) return prev
                const secondsRemaining = result.seconds_remaining ?? prev.seconds_remaining
                const exhausted = secondsRemaining <= 0 || !result.can_click
                return {
                  ...prev,
                  user_score: result.score,
                  can_click: result.can_click,
                  is_game_active: result.is_game_open,
                  seconds_remaining: secondsRemaining,
                  time_exhausted: exhausted,
                  can_watch_ad: exhausted ? true : prev.can_watch_ad,
                  cooldown_seconds_remaining: exhausted && prev.cooldown_seconds_remaining <= 0
                    ? 5 * 60
                    : prev.cooldown_seconds_remaining,
                }
              })
              pending.resolve(result)
            }
            return
          }

          if (msg.type === 'error') {
            if (msg.request_id) {
              const pending = pendingClicksRef.current.get(msg.request_id)
              if (pending) {
                clearTimeout(pending.timer)
                pendingClicksRef.current.delete(msg.request_id)
                pending.resolve(null)
              }
            }
            setError(msg.error || 'خطا در ثبت کلیک')
            return
          }

          if (msg.type === 'score_update' && msg.user_id !== undefined && msg.score !== undefined) {
            const storedUserID = localStorage.getItem('user_id')
            const currentUserID = storedUserID === null ? NaN : Number(storedUserID)
            if (Number.isFinite(currentUserID) && msg.user_id === currentUserID) {
              const broadcast: WSBroadcastScore = {
                type: 'score_update',
                user_id: msg.user_id,
                score: msg.score ?? 0,
              }
              setLiveScore(broadcast)
              setStatus(prev => prev ? {
                ...prev,
                user_score: msg.score ?? 0,
              } : prev)
            }
          }

          if (msg.type === 'leaderboard_update' && msg.data?.leaderboard) {
            const raw = msg.data.leaderboard as AirdropLeaderboardEntry[]
            const seen = new Set<number>()
            const unique: AirdropLeaderboardEntry[] = []
            for (const entry of raw || []) {
              if (seen.has(entry.user_id)) continue
              seen.add(entry.user_id)
              unique.push({ ...entry, rank: unique.length + 1 })
            }
            setLeaderboard(unique)
          }
        } catch (err) {
          console.error('WebSocket message parse error:', err)
        }
      }

      socket.onerror = (err: Event) => {
        console.error('WebSocket error:', err)
      }

      socket.onclose = () => {
        if (wsRef.current !== socket) return
        wsRef.current = null
        if (ws === socket) ws = null
        if (disposed) return

        setIsConnected(false)
        failPendingClicks('ارتباط وب‌سوکت قطع شد')
        scheduleReconnect()
      }
    }

    const scheduleReconnect = () => {
      if (disposed) return
      const attempt = reconnectAttemptRef.current
      if (attempt >= WS_RECONNECT_MAX_ATTEMPTS) {
        setError('ارتباط وب‌سوکت برقرار نشد')
        return
      }

      const delay = Math.min(WS_RECONNECT_INITIAL_DELAY * 2 ** attempt, WS_RECONNECT_MAX_DELAY)
      reconnectAttemptRef.current = attempt + 1
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null
        connect()
      }, delay)
    }

    const reconnectNow = () => {
      if (disposed) return
      const current = wsRef.current
      if (current && (current.readyState === WebSocket.OPEN || current.readyState === WebSocket.CONNECTING || current.readyState === WebSocket.CLOSING)) return

      clearReconnectTimer()
      reconnectAttemptRef.current = 0
      connect()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') reconnectNow()
    }

    const handleFocus = () => reconnectNow()

    connect()
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    return () => {
      disposed = true
      clearReconnectTimer()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      const socket = wsRef.current
      wsRef.current = null
      if (ws === socket) ws = null
      if (socket) {
        socket.onopen = null
        socket.onmessage = null
        socket.onerror = null
        socket.onclose = null
        socket.close()
      }
      clearPendingClicks()
    }
  }, [clearPendingClicks, failPendingClicks])

  useEffect(() => {
    fetchStatus()
    fetchWallet()
    void preloadRewardedAd()
  }, [fetchStatus, fetchWallet])

  const presenceStartRef = useRef<(() => Promise<void>) | null>(null)
  const adWatchInProgressRef = useRef(false)

  // 5-minute useful window: counts only while this page is visible.
  useEffect(() => {
    let disposed = false
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null
    let active = false

    const applyStatus = (data: AirdropStatusResponse) => {
      if (disposed) return
      // Don't let presence stop during an ad overwrite a fresh refill.
      if (adWatchInProgressRef.current && data.time_exhausted) return
      setStatus(data)
    }

    const stopPresence = async (opts?: { force?: boolean }) => {
      if (adWatchInProgressRef.current && !opts?.force) return
      if (!active) return
      active = false
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer)
        heartbeatTimer = null
      }
      try {
        const data = await airdropApi.stopPresence()
        applyStatus(data)
      } catch {
        /* ignore — page may already be closed */
      }
    }

    const startPresence = async () => {
      if (disposed || adWatchInProgressRef.current) return
      if (document.visibilityState !== 'visible') return
      try {
        const data = await airdropApi.startPresence()
        applyStatus(data)
        if (data.time_exhausted || !data.is_game_active || !data.can_click) {
          active = false
          if (heartbeatTimer) {
            clearInterval(heartbeatTimer)
            heartbeatTimer = null
          }
          return
        }
        active = true
        if (heartbeatTimer) clearInterval(heartbeatTimer)
        heartbeatTimer = setInterval(async () => {
          if (disposed || adWatchInProgressRef.current) return
          if (document.visibilityState !== 'visible') return
          try {
            const next = await airdropApi.heartbeatPresence()
            applyStatus(next)
            if (next.time_exhausted || !next.is_game_active || !next.can_click) {
              await stopPresence()
            }
          } catch (err: any) {
            const msg = err.response?.data?.message || ''
            if (msg) setError(msg)
          }
        }, 15000)
      } catch (err: any) {
        // Closed window / exhausted time still return 200 from start; only real failures land here.
        console.warn('airdrop presence start failed', err)
      }
    }

    presenceStartRef.current = startPresence

    const syncPresence = () => {
      if (adWatchInProgressRef.current) return
      if (document.visibilityState === 'visible') {
        void startPresence()
      } else {
        void stopPresence()
      }
    }

    const handleVisibility = () => syncPresence()
    const handlePageHide = () => { void stopPresence() }

    syncPresence()
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('pagehide', handlePageHide)

    return () => {
      disposed = true
      presenceStartRef.current = null
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('pagehide', handlePageHide)
      if (heartbeatTimer) clearInterval(heartbeatTimer)
      if (active) {
        void airdropApi.stopPresence().catch(() => undefined)
      }
    }
  }, [])

  // Resume presence after cooldown ends or ad refill.
  useEffect(() => {
    if (adWatchInProgressRef.current) return
    if (!status?.can_click || status.time_exhausted || !status.is_game_active) return
    if (status.presence_active) return
    if (document.visibilityState !== 'visible') return
    void presenceStartRef.current?.()
  }, [status?.can_click, status?.time_exhausted, status?.is_game_active, status?.presence_active])

  const watchAd = useCallback(async (): Promise<AirdropStatusResponse | null> => {
    adWatchInProgressRef.current = true
    try {
      setError(null)
      const ad = await showRewardedAd()
      if (!ad.ok) {
        setError(adFailMessage(ad.reason))
        return null
      }
      const data = await airdropApi.watchAd()
      setStatus(data)
      return data
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ثبت تبلیغ')
      return null
    } finally {
      adWatchInProgressRef.current = false
      if (document.visibilityState === 'visible') {
        await presenceStartRef.current?.()
      }
    }
  }, [])

  const refetch = useCallback(() => fetchStatus({ silent: true }), [fetchStatus])

  return {
    status,
    loading,
    error,
    wallet,
    leaderboard,
    liveScore,
    isConnected,
    click,
    watchAd,
    fetchLeaderboard,
    fetchMonthlyLeaderboard,
    refetch,
  }
}
